import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import MainLayout from '../layouts/MainLayout';
import portalTheme from '../theme/portalTheme';
import { ROUTES } from '../constants/routes';
import { shouldHoldSite } from './holding';

// Public pages
import Home from '../pages/Home';
import Tests from '../pages/Tests';
import TestDetail from '../pages/TestDetail';
import Packages from '../pages/Packages';
import PackageDetail from '../pages/PackageDetail';
import Specials from '../pages/Specials';
import Membership from '../pages/Membership';
import About from '../pages/About';
import Support from '../pages/Support';
import Centres from '../pages/Centres';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import Careers from '../pages/Careers';
import Corporate from '../pages/Corporate';
import OurDoctors from '../pages/OurDoctors';
import Cart from '../pages/Cart';
import Booking from '../pages/Booking';
import CancelBooking from '../pages/CancelBooking';
import Track from '../pages/Track';
import AppRedirect from '../pages/AppRedirect';
import ComingSoon from '../pages/ComingSoon';
import Partners from '../pages/Partners';
import PartnerSegment from '../pages/PartnerSegment';
import Franchise from '../pages/Franchise';
import Legal from '../pages/Legal';
import Help from '../pages/Help';
import City from '../pages/City';
import Guides from '../pages/Guides';
import GuideArticle from '../pages/GuideArticle';
import DoctorConsult from '../pages/DoctorConsult';
import BuildPackage from '../pages/BuildPackage';
import NotFound from '../pages/NotFound';

// Auth + user
import Login from '../pages/Login';
import Signup from '../pages/Signup';
import Reports from '../pages/Reports';
import MyTickets from '../pages/MyTickets';
import Dashboard from '../pages/Dashboard';

// Portals
import MedicalDashboard from '../pages/portals/MedicalDashboard';
import LabDashboard from '../pages/portals/LabDashboard';
import CorporateDashboard from '../pages/portals/CorporateDashboard';
import QAPortal from '../pages/portals/QAPortal';
import AdminPortal from '../pages/portals/AdminPortal';
import SiteAdmin from '../pages/portals/SiteAdmin';

// The portals are not part of the website's v5 redesign, so they are pinned
// to a frozen copy of the theme the whole app used before it started. Without
// this they would follow every change made to theme.js for the website.
//
// Applied here, at the route, rather than inside PortalLayout — MedicalDashboard
// builds its own theme with createTheme(outer, {...}) from OUTSIDE PortalLayout,
// so a provider in there would sit below it and overwrite the AIMS palette.
// From the route, this becomes that `outer` and the AIMS overrides still win.
const Portal = ({ children }) => (
  <ThemeProvider theme={portalTheme}>{children}</ThemeProvider>
);

// Marks the website's standalone routes — the ones that render without
// MainLayout — as part of the site, so stylesheets can be scoped to
// `.tl-site` and never reach a portal. `display: contents` keeps the element
// out of layout entirely: it is a selector hook, not a box.
const SiteScope = ({ children }) => (
  <div className="tl-site tl-inner" style={{ display: 'contents' }}>{children}</div>
);

const AppRoutes = () => {
  // Read once per mount rather than per route: the flag is baked in at
  // build time and the preview token is resolved on first load, so this
  // cannot change between one route and the next.
  const held = shouldHoldSite();

  // Holding mode. Only the routes listed here survive — staff sign-in,
  // the six portals, and the two URLs the mobile app and the printed QR
  // codes depend on. Everything else, including the fallback, becomes
  // the holding page, so there is no path back into the shop.
  if (held) {
    return (
      <Routes>
        <Route path={ROUTES.LOGIN} element={<SiteScope><Login /></SiteScope>} />
        <Route path={ROUTES.APP_PRIVACY} element={<SiteScope><PrivacyPolicy /></SiteScope>} />
        <Route path={ROUTES.APP_DOWNLOAD} element={<SiteScope><AppRedirect /></SiteScope>} />

        <Route path={ROUTES.MED_DASHBOARD} element={<Portal><MedicalDashboard /></Portal>} />
        <Route path={ROUTES.LAB_DASHBOARD} element={<Portal><LabDashboard /></Portal>} />
        <Route path={ROUTES.CORP_DASHBOARD} element={<Portal><CorporateDashboard /></Portal>} />
        <Route path={ROUTES.QA_PORTAL} element={<Portal><QAPortal /></Portal>} />
        <Route path={ROUTES.ADMIN} element={<Portal><AdminPortal /></Portal>} />
        <Route path={ROUTES.SITE_ADMIN} element={<Portal><SiteAdmin /></Portal>} />

        <Route path="*" element={<SiteScope><ComingSoon /></SiteScope>} />
      </Routes>
    );
  }

  return (
  <Routes>
    {/* Pages with main layout (navbar + footer) */}
    <Route element={<MainLayout />}>
      <Route path={ROUTES.HOME} element={<Home />} />
      <Route path={ROUTES.TESTS} element={<Tests />} />
      <Route path={ROUTES.TEST_DETAIL} element={<TestDetail />} />
      <Route path={ROUTES.PACKAGES} element={<Packages />} />
      <Route path={ROUTES.PACKAGE_DETAIL} element={<PackageDetail />} />
      <Route path={ROUTES.SPECIALS} element={<Specials />} />
      <Route path={ROUTES.MEMBERSHIP} element={<Membership />} />
      <Route path={ROUTES.ABOUT} element={<About />} />
      <Route path={ROUTES.BLOG} element={<Guides />} />
      <Route path={ROUTES.BLOG_ARTICLE} element={<GuideArticle />} />
      <Route path={ROUTES.HELP} element={<Help />} />
      {/* Help is the reference's merge of these two. /support keeps the
          ticket flow that Help links out to; /faq has no counterpart in
          the reference, so it lands on Help. */}
      <Route path={ROUTES.FAQ} element={<Navigate to={ROUTES.HELP} replace />} />
      <Route path={ROUTES.SUPPORT} element={<Support />} />
      <Route path={ROUTES.CENTRES} element={<Centres />} />
      <Route path={ROUTES.LEGAL} element={<Legal />} />
      {/* The v5 reference has one Legal page. /privacy keeps working for
          anything already linking to it. APP_PRIVACY below is untouched:
          it is the chrome-less URL on the two store listings. */}
      <Route path={ROUTES.PRIVACY} element={<Navigate to={`${ROUTES.LEGAL}#privacy`} replace />} />
      <Route path={ROUTES.CAREERS} element={<Careers />} />
      <Route path={ROUTES.CORPORATE} element={<Corporate />} />
      <Route path={ROUTES.DOCTORS} element={<OurDoctors />} />
      <Route path={ROUTES.CART} element={<Cart />} />
      <Route path={ROUTES.BOOKING} element={<Booking />} />
      <Route path={ROUTES.CANCEL_BOOKING} element={<CancelBooking />} />
      <Route path={ROUTES.TRACK} element={<Track />} />
      <Route path={ROUTES.REPORTS} element={<Reports />} />
      <Route path={ROUTES.MY_TICKETS} element={<MyTickets />} />
      <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />

      {/* Reference pages not built yet. They sit inside MainLayout so the
          new header and footer frame them, and each carries the title the
          reference gives it. Swap the element as each page is built. */}
      <Route path={ROUTES.PARTNERS} element={<Partners />} />
      <Route path={ROUTES.PARTNER_DOCTORS} element={<PartnerSegment segment="doctors" />} />
      <Route path={ROUTES.PARTNER_LABS} element={<PartnerSegment segment="labs" />} />
      <Route path={ROUTES.PARTNER_MANAGED_LABS} element={<PartnerSegment segment="managed-labs" />} />
      <Route path={ROUTES.PARTNER_PHLEBOS} element={<PartnerSegment segment="phlebotomists" />} />
      <Route path={ROUTES.FRANCHISE} element={<Franchise />} />
      <Route path={ROUTES.CITY} element={<City />} />
      <Route path={ROUTES.BUILD_PACKAGE} element={<BuildPackage />} />
      <Route path={ROUTES.DOCTOR_CONSULT} element={<DoctorConsult />} />

      {/* A real 404, inside the layout so it keeps the navbar and footer —
          a dead end with no way out is worse than the wrong page. This
          used to redirect to Home, which swallowed every mistyped or dead
          link without telling anyone. */}
      <Route path="*" element={<NotFound />} />
    </Route>

    {/* Login / Signup (no shell) */}
    <Route path={ROUTES.LOGIN} element={<SiteScope><Login /></SiteScope>} />
    <Route path={ROUTES.SIGNUP} element={<SiteScope><Signup /></SiteScope>} />

    {/* Bare privacy policy — no navbar / footer. Submitted as the
        Play Store + App Store privacy URL for the mobile app. Same
        page component as the public /privacy route; the difference
        is just the missing MainLayout chrome. */}
    <Route path={ROUTES.APP_PRIVACY} element={<SiteScope><PrivacyPolicy /></SiteScope>} />

    {/* Destination of the printed download QR code. No layout — it
        redirects to the Play Store / App Store within a frame or two. */}
    <Route path={ROUTES.APP_DOWNLOAD} element={<SiteScope><AppRedirect /></SiteScope>} />

    {/* Portals (full-bleed, own shell) — all pinned to the frozen theme */}
    <Route path={ROUTES.MED_DASHBOARD} element={<Portal><MedicalDashboard /></Portal>} />
    <Route path={ROUTES.LAB_DASHBOARD} element={<Portal><LabDashboard /></Portal>} />
    <Route path={ROUTES.CORP_DASHBOARD} element={<Portal><CorporateDashboard /></Portal>} />
    <Route path={ROUTES.QA_PORTAL} element={<Portal><QAPortal /></Portal>} />
    <Route path={ROUTES.ADMIN} element={<Portal><AdminPortal /></Portal>} />
    <Route path={ROUTES.SITE_ADMIN} element={<Portal><SiteAdmin /></Portal>} />

  </Routes>
  );
};

export default AppRoutes;
